import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Check, LayoutGrid, ClipboardList, ChartColumn } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

const screens = [
  {
    title: 'Masalar',
    image: 'masalar',
    detail: 'Açık hesapları ve masalarınızın durumunu bir arada görün.',
    icon: LayoutGrid,
  },
  {
    title: 'Sipariş',
    image: 'siparis',
    detail: 'Masayı seçin, ürünleri ekleyin, siparişi takip edin.',
    icon: ClipboardList,
  },
  { title: 'Raporlar', image: 'raporlar', detail: 'Satışlarınızı rapor ekranından inceleyin.', icon: ChartColumn },
];

export default function Hero() {
  const [active, setActive] = useState(0);
  const screen = screens[active];
  return (
    <section id="home" className="bg-surface pb-10 pt-8 sm:pb-14 sm:pt-12 lg:pt-16">
      <div className="container mx-auto px-4">
        <div className="grid items-center gap-9 lg:grid-cols-[0.85fr_1.15fr] lg:gap-10">
          <div>
            <p className="mb-4 font-inter text-xs font-semibold uppercase tracking-[0.16em] text-brand-600">
              Restoran ve kafeler için Tilki
            </p>
            <h1 className="font-poppins text-[2.4rem] font-bold leading-[1.08] tracking-tight text-ink-strong sm:text-5xl lg:text-[3.3rem]">
              Siparişten adisyona,
              <br />
              <span className="text-brand-600">kontrol sizde.</span>
            </h1>
            <p className="mt-5 max-w-lg font-inter text-lg leading-relaxed text-ink-muted">
              Restoran ve kafenizi tek bir bulut platformundan yönetin.
            </p>
            <ul className="mt-5 max-w-lg space-y-3 font-inter">
              {[
                ['Masa ve adisyon', 'Açık hesapları tek ekranda görün.'],
                ['QR menü ve sipariş', 'Menünüzü dijitale taşıyın, siparişleri takip edin.'],
                ['Sadakat ve kampanyalar', 'Müşterilerinize puan ve ödüller sunun.'],
                ['Satış raporları', 'İşletmenizin satışlarını takip edin.'],
              ].map(([title, detail]) => (
                <li key={title} className="flex items-start gap-3 text-sm leading-relaxed sm:text-base">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  <span className="text-ink-muted">
                    <strong className="font-semibold text-ink">{title}</strong>
                    <span className="block">{detail}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col gap-3 sm:items-start">
              <a
                href="#demo-contact-form"
                className="modern-btn-large w-full sm:w-auto"
                onClick={() => {
                  trackEvent('hero-contact-click');
                  trackEvent('lead-cta-click', { placement: 'hero' });
                }}
              >
                Ücretsiz tanıtım isteyin <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </a>
              <Link
                to="/nasil-kullanilir"
                className="inline-flex items-center justify-center gap-2 rounded-lg px-2 py-2 font-inter text-sm font-medium text-ink focus-visible:outline focus-visible:outline-2"
                onClick={() => trackEvent('hero-guide-click')}
              >
                <BookOpen className="h-4 w-4" aria-hidden="true" /> Ekranlarla nasıl çalıştığını görün
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 border-t border-hairline pt-5 font-inter text-sm text-ink-muted">
              {['Kurulum ücreti yok', 'Aylık veya yıllık kullanım'].map((label) => (
                <span key={label} className="inline-flex items-center gap-2">
                  <Check className="h-4 w-4 text-brand-600" aria-hidden="true" />
                  {label}
                </span>
              ))}
            </div>
          </div>
          <div className="min-w-0 rounded-2xl border border-hairline bg-surface-2 p-2 shadow-card-lift sm:p-3">
            <div className="flex items-center justify-between px-2 pb-3 pt-1 font-inter text-xs text-ink-muted">
              <span className="font-semibold text-ink">Tilki’ye yakından bakın</span>
              <span>Ürün ekranları</span>
            </div>
            <div
              role="tablist"
              aria-label="Ürün ekranları"
              className="mb-2 grid grid-cols-3 gap-1 rounded-xl bg-white p-1"
            >
              {screens.map(({ title, icon: Icon }, index) => (
                <button
                  key={title}
                  type="button"
                  role="tab"
                  id={`product-tab-${index}`}
                  aria-selected={active === index}
                  aria-controls="product-screen"
                  onClick={() => {
                    setActive(index);
                    trackEvent('hero-product-tab', { screen: title });
                  }}
                  className={`flex items-center justify-center gap-2 rounded-lg px-2 py-3 font-inter text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 ${active === index ? 'bg-brand-600 text-white' : 'text-ink-muted hover:bg-surface-2'}`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {title}
                </button>
              ))}
            </div>
            <div id="product-screen" role="tabpanel" aria-labelledby={`product-tab-${active}`}>
              <a
                href={`/img/product/${screen.image}-desktop.png`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  event.currentTarget.href = event.currentTarget.querySelector('img').currentSrc;
                }}
                className="mx-auto block max-w-[260px] overflow-hidden rounded-lg border border-hairline bg-white sm:max-w-none"
                aria-label={`${screen.title} ekranını büyük görüntüle (yeni sekme)`}
              >
                <picture>
                  <source media="(max-width: 639px)" srcSet={`/img/product/${screen.image}-mobile.png`} />
                  <img
                    src={`/img/product/${screen.image}-desktop.png`}
                    alt={`Tilki ${screen.title.toLowerCase()} ekranı`}
                    width="1850"
                    height="947"
                    fetchPriority="high"
                    className="aspect-[380/843] w-full object-contain sm:aspect-[1850/947]"
                  />
                </picture>
              </a>
              <div className="px-2 py-4">
                <p className="font-inter text-sm font-medium text-ink">{screen.detail}</p>
                <p className="mt-2 font-inter text-xs text-ink-muted">
                  Demo işletmeden gerçek ekran görüntüsü · Büyütmek için dokunun
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-12 grid gap-6 border-t border-hairline pt-7 sm:grid-cols-3 lg:mt-16">
          {[
            ['01', 'Masanızdan başlayın', 'Masa durumunu görün, siparişinizi ilgili masaya ekleyin.'],
            ['02', 'Menünüzü dijitale taşıyın', 'Ürünlerinizi QR menü üzerinden müşterilerinize sunun.'],
            ['03', 'Satışınızı takip edin', 'Sipariş ve satış bilgilerinizi raporlardan inceleyin.'],
          ].map(([number, title, description]) => (
            <div key={number} className="flex gap-3">
              <span className="pt-1 font-poppins text-xs font-semibold text-brand-600">{number}</span>
              <div>
                <h2 className="font-poppins text-base font-semibold text-ink">{title}</h2>
                <p className="mt-2 font-inter text-sm leading-relaxed text-ink-muted">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
